import { LoadingButton } from '@mui/lab'
import { Alert, Box, Button, Stack, Typography } from '@mui/material'
import { TimelineCaregiverTeamMemberControlAdd } from 'features/Timeline/components/TimelineCaregiverTeamMemberAdd/TimelineCaregiverTeamMemberControlAdd'
import { useEffect, useState } from 'react'
import { IFamilyContext, IFamilyMemberDto, SubscriptionPlanType, UserSubscription } from 'types'
import { formatDate } from 'utils'
import { getPlanByType, getPlanDisplayName } from '../constants'
import { isFamilyPlan, isPartnerFunded } from '../utils'
import { FamilyMembersList } from './FamilyMembersList'
import { SubscriptionPlanSelect } from './SubscriptionPlanSelect'

/**
 * Active plan editor for caregivers. Key behaviors:
 * - `subscription.plan` is the billed plan from the API; `selectedPlan` is the dropdown (may differ until Change is applied).
 * - For active subs, the backend may schedule a change at period end (`pendingPlan` / `pendingPlanEffectiveAt`) instead of flipping `plan` immediately.
 * - While a pending change exists, the scheduled target plan is disabled in the select so it cannot be chosen again.
 * - A partner-funded subscription has no billing at all: it shows its dates and seats, nothing to change or cancel.
 * - Unsubscribe (paid owner only) turns auto-renewal off; the plan then stays until `currentPeriodEnd`.
 */
export function SubscriptionPlanSection({
  subscription,
  familyContext,
  familyMembers,
  onChangePlan,
  changePlanLoading = false,
  onRemoveFamilyMember,
  removingMemberId,
  onFamilyMemberAdded,
  onUnsubscribe,
}: {
  subscription: UserSubscription
  familyContext?: IFamilyContext | null
  /** Full members list (invited + active) for the Family group — owner view only. */
  familyMembers?: IFamilyMemberDto[]
  onChangePlan: (plan: SubscriptionPlanType) => void
  changePlanLoading?: boolean
  onRemoveFamilyMember?: (member: IFamilyMemberDto) => void
  removingMemberId?: number | null
  /** Refresh after the owner adds a member (care-team add reserves a Family seat on the backend). */
  onFamilyMemberAdded?: () => void | Promise<void>
  /** Paid owner asks to stop auto-renewal; hidden when not offered. */
  onUnsubscribe?: () => void
}) {
  const isFamilyOwner =
    !!familyContext && familyContext.role === 'owner' && isFamilyPlan(subscription.plan)
  const isFamilyMember = familyContext?.role === 'member'
  const isFunded = isPartnerFunded(subscription)
  // Auto-renewal already off: nothing to change, nothing to cancel again.
  const isCancelled = subscription.cancelAtPeriodEnd
  const canUnsubscribe = !!onUnsubscribe && !isFunded && !isFamilyMember && !isCancelled
  const seatsOccupied = familyContext
    ? familyContext.membersActiveCount + familyContext.membersInvitedCount
    : 0
  // Local dropdown value; differs from subscription.plan while the user is previewing another plan.
  const [selectedPlan, setSelectedPlan] = useState(subscription.plan)

  const currentPlanMeta = getPlanByType(subscription.plan)
  const selectedPlanMeta = getPlanByType(selectedPlan)
  const planChanged = selectedPlan !== subscription.plan

  // Stripe-scheduled next plan (set by backend when status is active and change is deferred to period end).
  const pendingEffectiveAt = subscription.pendingPlanEffectiveAt
  const pendingPlanType = subscription.pendingPlan
  const hasPendingChange = Boolean(pendingPlanType && pendingEffectiveAt)
  const pendingPlanMeta =
    pendingPlanType && hasPendingChange ? getPlanByType(pendingPlanType) : undefined

  // Reset dropdown after successful Change or when subscription data refreshes (e.g. pending row updated).
  useEffect(() => {
    setSelectedPlan(subscription.plan)
  }, [subscription.plan, subscription.pendingPlan])

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Active Plan: {getPlanDisplayName(subscription.plan)}
      </Typography>

      <Stack spacing={2}>
        {/* Family members don't manage billing — owner does. Show static info instead of plan editor. */}
        {isFamilyMember && <Alert severity="info">You are using a Family Subscription plan.</Alert>}

        {/* Partner-funded: fixed one-year period, no card, no renewal, no plan change. */}
        {isFunded && (
          <>
            <Alert severity="info">
              Your Family Subscription is funded by a partner organization. No payment is required;
              access ends on the expiration date below.
            </Alert>

            <Stack direction="row" spacing={4}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Subscription Start Date
                </Typography>
                <Typography variant="body1">
                  {formatDate(subscription.currentPeriodStart, 'MM.DD.YY')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Subscription Expiration Date
                </Typography>
                <Typography variant="body1">
                  {formatDate(subscription.currentPeriodEnd, 'MM.DD.YY')}
                </Typography>
              </Box>
            </Stack>
          </>
        )}

        {/* Paid subscription with auto-renewal turned off: read-only until the period ends. */}
        {!isFunded && !isFamilyMember && isCancelled && (
          <Alert severity="warning">
            Auto-renewal is off. Your subscription remains active until{' '}
            {formatDate(subscription.currentPeriodEnd, 'MM.DD.YY')}; you will not be charged again.
          </Alert>
        )}

        {!isFunded && !isFamilyMember && !isCancelled && (
          <>
            <Stack direction="row" spacing={1} alignItems="center">
              {/* Disable the option that matches pendingPlan so users cannot "re-select" an already scheduled target. */}
              <SubscriptionPlanSelect
                value={selectedPlan}
                onChange={setSelectedPlan}
                disabled={changePlanLoading}
                disabledPlanType={hasPendingChange ? pendingPlanType : undefined}
              />

              <LoadingButton
                variant="contained"
                size="large"
                onClick={() => onChangePlan(selectedPlan)}
                loading={changePlanLoading}
                disabled={!planChanged}
              >
                Change
              </LoadingButton>
            </Stack>

            {/* currentPeriodEnd: end of the billed period; when pending exists, same boundary as when the scheduled plan starts. */}
            <Box>
              <Typography variant="body2" color="text.secondary">
                {hasPendingChange ? 'Current period ends' : 'Renewal date'}
              </Typography>
              <Typography variant="body1">
                {formatDate(subscription.currentPeriodEnd, 'MM.DD.YY')}
              </Typography>
            </Box>

            {/* Explains current vs scheduled plan; shown only when backend has set pending fields. */}
            {hasPendingChange && pendingPlanMeta && currentPlanMeta && pendingEffectiveAt && (
              <Alert severity="info" sx={{ alignItems: 'flex-start' }}>
                <Typography variant="body2" component="span" display="block">
                  You are on <strong>{currentPlanMeta.label}</strong> (${currentPlanMeta.price} USD)
                  through {formatDate(pendingEffectiveAt, 'MM.DD.YY')}. On that date, your
                  subscription switches to <strong>{pendingPlanMeta.label}</strong> ($
                  {pendingPlanMeta.price} USD).
                </Typography>
              </Alert>
            )}

            {/* No pending schedule: show renewal copy. If the dropdown differs from the saved plan, strike through the old amount and emphasize the selected amount. */}
            {!hasPendingChange && selectedPlanMeta && (
              <Typography variant="body2" color="text.secondary" component="div">
                Your plan will automatically renew for{' '}
                {planChanged && currentPlanMeta ? (
                  <>
                    <Box component="span" sx={{ textDecoration: 'line-through', opacity: 0.75 }}>
                      ${currentPlanMeta.price}
                    </Box>{' '}
                    <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      ${selectedPlanMeta.price}
                    </Box>
                  </>
                ) : (
                  <>${selectedPlanMeta.price}</>
                )}{' '}
                on the renewal date.
              </Typography>
            )}

            {/* Pending: next charge aligns with period boundary and pending plan price (renewal line above is hidden to avoid contradicting the schedule). */}
            {hasPendingChange && pendingPlanMeta && pendingEffectiveAt && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Next payment
                </Typography>
                <Typography variant="body1">
                  ${pendingPlanMeta.price} on {formatDate(pendingEffectiveAt, 'MM.DD.YY')}
                </Typography>
              </Box>
            )}

            {canUnsubscribe && (
              <Box>
                <Button variant="outlined" color="error" onClick={onUnsubscribe}>
                  Unsubscribe
                </Button>
              </Box>
            )}
          </>
        )}

        {/* Family seats counter — for the Family owner, paid or partner-funded; counts confirmed + pending invites. */}
        {isFamilyOwner && familyContext && (
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Family Members
                </Typography>
                <Typography variant="body1">
                  <strong>
                    {seatsOccupied} of {familyContext.maxMembers} invited
                  </strong>
                </Typography>
              </Box>

              {/* Reuses the care-team add flow; backend reserves a Family seat for an owner with seats left. */}
              {familyContext.canInvite && (
                <TimelineCaregiverTeamMemberControlAdd onAfterAdd={onFamilyMemberAdded} />
              )}
            </Stack>

            {familyMembers && onRemoveFamilyMember && (
              <FamilyMembersList
                members={familyMembers}
                onRemove={onRemoveFamilyMember}
                removingMemberId={removingMemberId}
              />
            )}
          </Box>
        )}
      </Stack>
    </Box>
  )
}
